import {useMutation, useQuery} from '@tanstack/react-query';
import {t} from 'i18next';
import {
  IAdvancedSearchTabsDataResponse,
  IContentMDValidationResponse,
  ISearchValidationResponse,
  ReloadSearchResultResponse,
  SearchRequest,
  SearchResponse,
  SearchResultRequest,
  IRunConceptualSearchResponse,
  IConceptualSearchStatusResponse,
  ISaveResponse,
  ISearchStatusResponse,
  SessionSearchItem,
  ISearchUniqueDocIds,
  ISearchUniqueCountRequest,
  ISearchUniqueCountResponse,
  GetNestedSearchesParams,
  GetNestedSearchesResponse,
  AddSearchMessageToQueueRequest,
  IValidateCriteriaRequest,
} from '../../api/types/sessionSearch.type';
import {
  getAdvancedSearchTabsData,
  getOperatorData,
  getAutocomplete,
  getUserList,
  validateAnalyticCriteria,
  validateWorkProductCriteria,
  validateContentmetaData,
  validateSearchCriteria,
  runSearch,
  addSearchToQueue,
  reloadSearchResult,
  runConceptualSearch,
  getConceptualSearchStatus,
  saveSearch,
  getSearchStatus,
  notification,
  getSearchUniqueDocIds,
  getSearchUniqueCount,
  getNestedSearches,
} from '../../api/endpoints/sessionSearchApi';

export const useFieldData = (fieldType: string, indexID?: number) => {
  return useQuery<IAdvancedSearchTabsDataResponse, Error>({
    queryKey: ['search.session.tabData', fieldType, indexID],
    queryFn: () =>
      indexID === undefined
        ? getAdvancedSearchTabsData(fieldType)
        : getAdvancedSearchTabsData(fieldType, indexID),
  });
};

export const useOperatorData = (dataType: string) => {
  return useQuery({
    queryKey: ['search.session.dataType', dataType],
    queryFn: () => getOperatorData(dataType),
    enabled: !!dataType,
  });
};

export const useAutocomplete = (text: string, isComments: boolean = false) => {
  const t = isComments ? 'COMMENTS' : 'TEXT';
  return useQuery<string[], Error>({
    queryKey: ['search.session.autocomplete'],
    queryFn: () => getAutocomplete(text, t),
  });
};

export const useUserList = () => {
  return useQuery({
    queryKey: ['search.session.userList'],
    queryFn: () => getUserList(),
  });
};

export const useValidateAnalyticCriteria = (
  onSuccess?: (data: ISearchValidationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ISearchValidationResponse, Error, string>({
    mutationFn: validateAnalyticCriteria,
    mutationKey: ['search.validation.analytics'],
    onSuccess,
    onError,
  });
};

export const useGetSearchUniqueDocIds = (
  onSuccess?: (data: ISearchUniqueDocIds) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISearchUniqueDocIds,
    Error,
    {searchId: number; redirectToSearch: string; searchName: string}
  >({
    mutationFn: ({searchId, redirectToSearch, searchName}) =>
      getSearchUniqueDocIds(searchId, redirectToSearch, searchName),
    mutationKey: ['search.uniqueDocIds'],
    onSuccess,
    onError,
  });
};

export const useGetNestedSearches = (
  onSuccess?: (data: GetNestedSearchesResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<GetNestedSearchesResponse, Error, GetNestedSearchesParams>(
    {
      mutationFn: getNestedSearches,
      mutationKey: ['search.nestedSearches'],
      onSuccess,
      onError,
    }
  );
};

export const useGetSearchUniqueCount = (
  onSuccess?: (data: ISearchUniqueCountResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ISearchUniqueCountResponse,
    Error,
    ISearchUniqueCountRequest[]
  >({
    mutationFn: getSearchUniqueCount,
    mutationKey: ['search.uniqueCount'],
    onSuccess,
    onError,
  });
};

export const useValidateWorkProductCriteria = (
  onSuccess?: (data: ISearchValidationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ISearchValidationResponse, Error, string>({
    mutationFn: validateWorkProductCriteria,
    mutationKey: ['search.validation.workproduct'],
    onSuccess,
    onError,
  });
};

export const useValidateContentmetaData = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IContentMDValidationResponse,
    Error,
    IValidateCriteriaRequest
  >({
    mutationFn: validateContentmetaData,
    mutationKey: ['search.validation.ContentmetaData'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useValidateSearchCriteria = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<boolean, Error, IValidateCriteriaRequest>({
    mutationFn: validateSearchCriteria,
    mutationKey: ['search.validation.searchcriteria'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRunSearch = (
  onSuccess?: (data: GetNestedSearchesResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<GetNestedSearchesResponse, Error, SearchRequest>({
    mutationFn: async (request: SearchRequest) => {
      try {
        return await runSearch(request);
      } catch (err: any) {
        // Normalize error so React Query always gets an Error object
        const message =
          err?.response?.status === 500
            ? 'Run search failed: Internal Server Error (500)'
            : err?.message || 'Run search failed: Unknown error';
        throw new Error(message);
      }
    },
    mutationKey: ['search.runsearch'],
    onSuccess,
    onError,
  });
};

export const useAddSearchToQueue = (
  onSuccess?: (data: SearchResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<SearchResponse, Error, AddSearchMessageToQueueRequest>({
    mutationFn: async (request: AddSearchMessageToQueueRequest) => {
      try {
        return await addSearchToQueue(request);
      } catch (err: any) {
        const message =
          err?.response?.status === 500
            ? t('sessionSearch.errors.addSearchToQueueInternalServerError')
            : err?.message ||
              t('sessionSearch.errors.addSearchToQueueUnknownError');
        throw new Error(message);
      }
    },
    mutationKey: ['search.addSearchToQueue'],
    onSuccess,
    onError,
  });
};

export const useReloadSearchResult = (
  onSuccess?: (data: ReloadSearchResultResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ReloadSearchResultResponse, Error, SearchResultRequest>({
    mutationFn: async (request: SearchResultRequest) => {
      try {
        return await reloadSearchResult(request);
      } catch (err: any) {
        const message =
          err?.response?.status === 500
            ? 'Reload search result failed: Internal Server Error (500)'
            : err?.message || 'Run search failed: Unknown error';
        throw new Error(message);
      }
    },
    mutationKey: ['search.reloadSearchResult'],
    onSuccess,
    onError,
  });
};

export const useRunConceptualSearch = (
  onSuccess?: (data: IRunConceptualSearchResponse) => void,
  onError?: (err: Error) => void,
  onSettled?: (
    data: IRunConceptualSearchResponse | undefined,
    err: Error | null
  ) => void
) => {
  return useMutation({
    mutationFn: runConceptualSearch,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
    onSettled: (data, error) => {
      onSettled?.(data, error);
    },
  });
};

export const useGetConceptualSearchStatus = (
  onSuccess?: (data: IConceptualSearchStatusResponse) => void,
  onError?: (err: Error) => void,
  onSettled?: (
    data: IConceptualSearchStatusResponse | undefined,
    err: Error | null
  ) => void
) => {
  return useMutation({
    mutationFn: getConceptualSearchStatus,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
    onSettled: (data, error) => {
      onSettled?.(data, error);
    },
  });
};

export const useSaveSearch = (
  onSuccess?: (data: ISaveResponse) => void,
  onError?: (err: Error) => void,
  onSettled?: (data: ISaveResponse | undefined, err: Error | null) => void
) => {
  return useMutation({
    mutationFn: saveSearch,
    mutationKey: ['search.save'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
    onSettled: (data, error) => {
      onSettled?.(data, error);
    },
  });
};

export const useSearchStatus = (searchId: number) => {
  return useQuery<ISearchStatusResponse, Error>({
    queryKey: ['search.session.searchStatus', searchId],
    queryFn: () => getSearchStatus(searchId),
    enabled: !!searchId,
  });
};

export const useAddNotification = () => {
  return useMutation({
    mutationFn: ({
      searchId,
      notificationType,
      searchNotifyType,
      sessionSearch,
    }: {
      searchId: number;
      notificationType: string;
      searchNotifyType: string;
      sessionSearch: SessionSearchItem;
    }) =>
      notification(searchId, notificationType, searchNotifyType, sessionSearch),
  });
};
